/**
 * The taxonomy every analytics metric is tagged against.
 *
 * There are FOUR independent fields, not one flat tag list, because the consumers
 * ask different questions and a single list cannot answer them without lying:
 *
 *   - `solutions` — "what does the marketing site's <solution> page prove?"
 *   - `roles`     — "which analytics tab does this belong on?"
 *   - `evidences` — "which capability does this metric provide evidence for?"
 *   - `blocked`   — "does the source data exist at all?"  (the A/B/C tier DERIVES
 *                   from this plus `inputs` — see `metricTier()`)
 *
 * Solutions and roles genuinely cross-product: Legal cares about Brand Protection
 * AND Compliance; Brand Protection matters to Legal AND Marketing. Collapsing them
 * into one list would force a choice that misrepresents both.
 */

/**
 * Product solution areas. These are the marketing site's top-level groupings, so
 * the string values are the customer-facing labels verbatim — changing one changes
 * a published page.
 */
export enum MetricSolution {
  ResaleCircularity = 'resale_circularity',
  BrandProtection = 'brand_protection',
  TraceabilityLogistics = 'traceability_logistics',
  AiAnalytics = 'ai_analytics',
  ConsumerEngagement = 'consumer_engagement',
  SustainabilityCompliance = 'sustainability_compliance',
}

export const METRIC_SOLUTION_LABELS: Record<MetricSolution, string> = {
  [MetricSolution.ResaleCircularity]: 'Resale & Circularity',
  [MetricSolution.BrandProtection]: 'Brand Protection',
  [MetricSolution.TraceabilityLogistics]: 'Traceability & Logistics',
  [MetricSolution.AiAnalytics]: 'AI & Analytics',
  [MetricSolution.ConsumerEngagement]: 'Consumer Engagement',
  [MetricSolution.SustainabilityCompliance]: 'Sustainability & Compliance',
};

/**
 * The company-side roles a metric can be surfaced to. Deliberately a SUBSET of
 * `RoleName`: platform/internal roles and the Owner role are excluded, because a
 * per-role analytics tab is a company-user surface. Values mirror `RoleName` so a
 * profile's role maps straight onto a tab with no translation table.
 */
export enum MetricRole {
  Admin = 'company_admin',
  TeamMember = 'company_team_member',
  Finance = 'company_finance',
  Design = 'company_design',
  Analytics = 'company_analytics',
  Marketing = 'company_marketing',
  Logistics = 'company_logistics',
  Purchasing = 'company_purchasing',
  Engineering = 'company_engineering',
  Legal = 'company_legal',
}

export const METRIC_ROLE_LABELS: Record<MetricRole, string> = {
  [MetricRole.Admin]: 'Admin',
  [MetricRole.TeamMember]: 'Team',
  [MetricRole.Finance]: 'Finance',
  [MetricRole.Design]: 'Design',
  [MetricRole.Analytics]: 'Analytics',
  [MetricRole.Marketing]: 'Marketing',
  [MetricRole.Logistics]: 'Logistics',
  [MetricRole.Purchasing]: 'Purchasing',
  [MetricRole.Engineering]: 'Engineering',
  [MetricRole.Legal]: 'Legal',
};

/**
 * What Vera DOES — the capability a metric provides evidence for.
 *
 * A capability is not a metric. "Marketplace Listing Takedowns" is something the
 * product performs; the metric that proves it worked is a separate row. Keeping
 * them apart is what lets one capability page pull several proof metrics, and one
 * metric evidence several capabilities, without either knowing about pages.
 */
export enum MetricCapability {
  ResaleCustomerCapture = 'resale_customer_capture',
  ReturnFraudElimination = 'return_fraud_elimination',
  StolenProductPrevention = 'stolen_product_prevention',
  BrandProtectionSoftware = 'brand_protection_software',
  CounterfeitMarketAnalysis = 'counterfeit_market_analysis',
  CounterfeitLitigation = 'counterfeit_litigation',
  MarketplaceListingTakedowns = 'marketplace_listing_takedowns',
  CpaAuthentication = 'cpa_authentication',
  CpaAdministration = 'cpa_administration',
  InfluencerIdentification = 'influencer_identification',
  DppCompliance = 'dpp_compliance',
  LoiAgec = 'loi_agec',
  SalespersonEngagementAnalysis = 'salesperson_engagement_analysis',
  MarketDemandAnalysis = 'market_demand_analysis',
  OwnerUpsellOnResale = 'owner_upsell_on_resale',
  NewProductSales = 'new_product_sales',
  ResaleMonetization = 'resale_monetization',
  GenerativeAiInsights = 'generative_ai_insights',
  SupplyChainMonitoring = 'supply_chain_monitoring',
  InventoryManagement = 'inventory_management',
  DigitalExperienceDesigner = 'digital_experience_designer',
  RepairProgramAuthentication = 'repair_program_authentication',
  RepairProgramAdministration = 'repair_program_administration',
}

/**
 * How real a metric's number can be TODAY.
 *
 * ⚠️ DERIVED, NEVER STORED. Call `metricTier(metric)`.
 *
 * Three states, and only ONE of them is stored:
 *
 *   Derived               — nothing asked for, nothing assumed.
 *   NeedsInput            — every key in `inputs` must be set first.
 *   SourceNotInstrumented — `blocked` is set; no input can rescue it.
 *
 * An earlier version stored this alongside `inputs`, and a guard asserted
 * `tier === Derived ⟺ inputs.length === 0` — which is the tell that the two fields
 * encoded one fact. Storing it twice means they can disagree, and the only defence
 * is a test keeping the copies in sync. `blocked` answers the one genuinely
 * independent question (does the source data exist?); the rest falls out of
 * `inputs`. TypeScript now rejects a stored `tier` outright.
 */
export enum MetricTier {
  /** Computed end to end from recorded facts. Nothing is assumed or asked for. */
  Derived = 'derived',
  /** Computable once the brand supplies every key in `inputs`. */
  NeedsInput = 'needs_input',
  /** The source events are not captured, so no input can rescue it. */
  SourceNotInstrumented = 'source_not_instrumented',
}

export const METRIC_TIER_LABELS: Record<MetricTier, string> = {
  [MetricTier.Derived]: 'Derived',
  [MetricTier.NeedsInput]: 'Needs input',
  [MetricTier.SourceNotInstrumented]: 'Source not instrumented',
};

/**
 * Brand-supplied assumptions. Collected at signup where possible, editable in
 * Settings, and surfaced inline on any card that needs one (never hidden in a
 * tooltip) — a dollar figure resting on an unstated assumption is worse than no
 * figure, most of all on a provenance product.
 */
export enum MetricInputKey {
  /** Share of invalid scans that represent a genuine counterfeit encounter. */
  CounterfeitConversionRate = 'counterfeit_conversion_rate',
  /** What the brand pays to acquire one customer. */
  CustomerAcquisitionCost = 'customer_acquisition_cost',
  /** Expected lifetime value of one captured customer. */
  CustomerLifetimeValue = 'customer_lifetime_value',
  /**
   * Annual value of goods the brand loses to return fraud it does NOT currently
   * catch — the returns it wrongly accepts, where the goods are simply gone.
   *
   * A VALUE, not a rate. The brand knows this figure (or can size it) directly;
   * asking for a rate would require us to also know return volume, which we do
   * not, and would turn one honest input into two guesses.
   */
  ReturnFraudAnnualLoss = 'return_fraud_annual_loss',
  /** Gross margin, for converting recovered/retained units into profit. */
  GrossMarginRate = 'gross_margin_rate',
}

/**
 * How a stored input value should be read.
 *
 * Stored WITH the value rather than looked up from the key at read time, and that
 * is deliberate: it makes a reinterpretation of an input's unit detectable instead
 * of silent. If `CustomerAcquisitionCost` were ever redefined from minor units to
 * major, every historic row would keep saying `currency_minor` and a reader could
 * tell the old numbers from the new ones. Without it, the same integer means two
 * different amounts depending on when it was written, and nothing on the row says
 * which — a 100x error with no evidence at the scene.
 */
export type MetricInputUnit = 'rate' | 'currency_minor';

/**
 * Where a value came from. Drives the provenance line on the settings page and,
 * with `provisional`, whether the number is allowed to drive a headline figure
 * without a human having looked at it.
 */
export type MetricInputProvenance = 'signup' | 'user' | 'vera_default';

/**
 * A value supplied by the brand.
 *
 * `provisional` is load-bearing: values pre-filled from the website signup form
 * arrive UNCONFIRMED, and must be visibly distinguishable from a number someone
 * deliberately chose in-app. A sales-form figure silently driving a headline ROI
 * number is how a brand ends up disputing its own dashboard.
 *
 * `set_by_user_id` / `set_at` exist because the failure mode on this page is a
 * brand disputing its own dashboard, and "who entered this, and when" is the first
 * question asked in that conversation. A dollar figure with no author is not
 * auditable, and this is a provenance product.
 */
export interface IMetricInputValue {
  key: MetricInputKey;
  /** Rates are stored 0–1, currency in minor units of `currency`. */
  value: number;
  /** How to read `value`. See `MetricInputUnit`. */
  unit: MetricInputUnit;
  /** ISO 4217, present only when `unit` is `currency_minor`. */
  currency?: string | null;
  /** True until a human confirms it in-app. Signup-supplied values start true. */
  provisional: boolean;
  /** Where it came from, for the provenance line on the settings page. */
  source: MetricInputProvenance;
  /** Null for `vera_default` and `signup`. */
  set_by_user_id?: string | null;
  set_at?: string | null;
}

export const METRIC_INPUT_LABELS: Record<MetricInputKey, string> = {
  [MetricInputKey.CounterfeitConversionRate]: 'Counterfeit rate',
  [MetricInputKey.CustomerAcquisitionCost]: 'Customer acquisition cost',
  [MetricInputKey.CustomerLifetimeValue]: 'Customer lifetime value',
  [MetricInputKey.ReturnFraudAnnualLoss]: 'Annual return-fraud loss',
  [MetricInputKey.GrossMarginRate]: 'Gross margin',
};
